import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as MotorcycleIcon } from "../assests/nav/motorcycle.svg";
import { ReactComponent as Blocks } from "../assests/nav/block.svg";
import { ReactComponent as BookingIcon } from "../assests/nav/bookingIcon.svg";
import { ReactComponent as ScheduleIcon } from "../assests/nav/schedule.svg";
import { ReactComponent as AccessoriesIcon } from "../assests/nav/accessories.svg";
import { Layout, Menu, theme } from "antd";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import style from "../style/layouts.module.scss";
import TopHeader from "../components/TopHeader";
import useIsMobile from "../utils/useIsMobile";

enum ROUTE {
  HOME = "",
  BIKES = "bikes",
  ACCESSORIES = "accessories",
  PARTNER_BIKES_BLOCKS = "blocks",
  BOOKING = "booking",
  SCHEDULE = "schedule",
}

const DASHBOARD_NAV_BAR = [
  {
    label: "Bikes",
    key: ROUTE.BIKES,
    icon: <MotorcycleIcon style={{ width: "30px" }} />,
  },
  {
    label: "Blocks",
    key: ROUTE.PARTNER_BIKES_BLOCKS,
    icon: (
      <div className={style["block_icon"]}>
        <Blocks width={50} height={50} />
      </div>
    ),
  },
  {
    label: "Booking",
    key: ROUTE.BOOKING,
    icon: <BookingIcon />,
  },
  {
    label: "Schedule",
    key: ROUTE.SCHEDULE,
    icon: <ScheduleIcon />,
  },
  {
    label: "Accessories",
    key: ROUTE.ACCESSORIES,
    icon: <AccessoriesIcon />,
  },
];

const { Header, Sider, Content } = Layout;

// Update the base path here
const BASE_PATH = "/partner/dashboard";

const getPathFromKey = (key: string): string => {
  return `${BASE_PATH}/${key}`;
};

const getKeyFromPath = (pathname: string): string => {
  const key = pathname.replace(`${BASE_PATH}/`, ""); // Remove base path
  return key || ROUTE.HOME; // Default to HOME if path is empty
};

const PartnerLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true); // Default collapsed (icons only)
  const [isHovered, setIsHovered] = useState(false);
  const siderRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const currentKey = getKeyFromPath(location.pathname); // Determine active tab based on the URL

  // For desktop: show expanded on hover, collapsed by default
  // For mobile: use collapsed state for toggle
  const isExpanded = isMobile ? !collapsed : isHovered;

  const handleMenuSelect = ({ key }: { key: string }) => {
    if (key !== currentKey) {
      // Only navigate if the selected tab is different from the current one
      navigate(getPathFromKey(key));
    }
  };

  // Handle click outside to close menu (only on mobile)
  useEffect(() => {
    if (!isMobile) return; // Don't close on click outside for desktop

    const handleClickOutside = (event: MouseEvent) => {
      if (collapsed) return;

      const target = event.target as HTMLElement;

      // Don't close if clicking on hamburger menu (check for MenuOutlined icon or its parent)
      const isHamburgerClick =
        target.closest('[class*="anticon-menu"]') ||
        target.closest('[class*="hamburger-menu"]') ||
        target.closest('[class*="left-container"]');

      if (isHamburgerClick) {
        return;
      }

      // Close menu if clicking outside the sidebar
      if (siderRef.current && !siderRef.current.contains(target)) {
        setCollapsed(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [collapsed, isMobile]);

  return (
    <Layout className={style["main-container"]}>
      <div
        ref={siderRef}
        className={isMobile && collapsed ? style["collapsed"] : style["not-collapsed"]}
        style={{
          transform: isMobile && collapsed ? "translateX(-100%)" : "translateX(0)",
          transition: "transform 0.3s ease-in-out",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
        <Sider
          collapsed={!isMobile && !isExpanded}
          width={220}
          collapsedWidth={80}
          trigger={null}
        >
          <Menu
            theme="dark"
            mode="inline"
            items={[...DASHBOARD_NAV_BAR]}
            selectedKeys={[currentKey]} // Highlight the tab based on the URL
            onSelect={handleMenuSelect}
          />
        </Sider>
      </div>
      {!collapsed && isMobile && (
        <div
          className={style["menu-overlay"]}
          onClick={() => setCollapsed(true)}
        />
      )}

      <Layout className={style["left-content"]}>
        <Header style={{ padding: 0, background: colorBgContainer }}>
          <div className={style["wrap-header"]}>
            <TopHeader
              setCollapsed={setCollapsed}
              collapsed={collapsed}
              isPartner
              currentKey={currentKey}
            />
          </div>
        </Header>

        <Content
          style={{
            minHeight: 280,
            background: "transparent",
            marginTop: "60px",
            marginLeft: !isMobile && isExpanded ? "220px" : !isMobile ? "80px" : undefined,
            transition: "margin-left 0.3s ease-in-out",
            overflow: "hidden",
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default PartnerLayout;
